/**
 * The v1 (Segment) event taxonomy: the object and action halves that compose
 * a legacy event name as `<object>_<action>`.
 *
 * Extracted from `@/models/analyticsScribeModel` so a call site can name a v1
 * event without importing the model — which pulls the whole analytics/store
 * graph in behind it. The model re-exports both for its existing importers.
 *
 * NEW EVENTS DO NOT BELONG HERE. The v1 path is deprecated; new events are
 * `TrackingEventName` members sent with `track()` (see `.ai-context/analytics.md`).
 */

export enum SegmentObjectName {
  General = 'general',
  Session = 'session',
  Template = 'template',
  Context = 'context',
  Document = 'document',
  Note = 'note',
  AskAi = 'askAi',
  Settings = 'settings',
  Integration = 'integration',
  Navigation = 'navigation',
  FreeToPaid = 'free_to_paid',
  SignIn = 'sign_in',
  SignUp = 'sign_up',
  ProductTour = 'product_tour',
  Integrations = 'integrations',
  Team = 'team',
  Referral = 'referral',
  Survey = 'survey',
  Account = 'account',
  Feedback = 'feedback',
  Paywall = 'paywall',
  GlobalConfig = 'global_config',
  Community = 'community',
  Link = 'link',
  Notification = 'notification',
  MedicalDirector = 'medical_director',
  EhrLaunch = 'ehr_launch',
  GettingStarted = 'getting_started',
  SectionalNote = 'sectional_note',

  ContextFiles = 'context_file',
  ReverseTrial = 'reverse_trial',
  CustomiseTemplateMenu = 'customise_template_menu',
  ChromeExtension = 'chrome_extension',

  Task = 'task',

  FormFill = 'form_fill',
  Pyne = 'pyne',

  Personalization = 'personalization',
  TemplateChatBuilder = 'template_chat_builder',
  Chat = 'chat',
  Evidence = 'evidence',
  GlobalTemplate = 'global_template',

  DesktopForceUpdate = 'desktop_force_update',
}

export enum SegmentActionName {
  // Medical Director Integration
  MedicalDirectorPatientSwitched = 'medical_director_patient_switched',
  MedicalDirectorProcessDemographicsResponseSent = 'medical_director_process_demographics_response',
  MedicalDirectorCreateNew = 'medical_director_create_new',
  MedicalDirectorEventSubscribed = 'medical_director_event_subscribed',
  MedicalDirectorGetPatientSessionsFailed = 'medical_director_get_patient_sessions_failed',
  MedicalDirectorSyncSessionsFailed = 'medical_director_sync_sessions_failed',
  MedicalDirectorGetDemographicsFailed = 'medical_director_get_demographics_failed',

  // EhrLaunch Integration
  SmartFhirLaunch = 'smart_fhir_launch',
  SmartFhirLaunchGenerateTokenError = 'smart_fhir_launch_generate_token_error',
  EhrLaunchStartWithLaunchToken = 'ehr_launch_start_with_launch_token',
  EhrLaunchGetAuthToken = 'ehr_launch_get_auth_token',
  EhrLaunchGetPatientDetails = 'ehr_launch_get_patient_details',

  // Sectional notes
  SectionalNoteEhrSectionSelected = 'ehr_section_selected',

  // Native authenticated (Desktop app/Mobile app)
  NATIVE_AUTHENTICATED = 'native_authenticated',
  TAURI_FFMPEG_INSTALLATION_FAILED = 'tauri_ffmpeg_installation_failed',
  TAURI_FFMPEG_CUSTOM_INSTALLATION_FAILED = 'tauri_ffmpeg_custom_installation_failed',
  TAURI_DEFAULT_DEVICE_ERROR = 'tauri_default_device_error',
  TAURI_GET_RECORDING_FAILED = 'tauri_get_recording_failed',
  TAURI_ERROR_EVENT = 'tauri_error_event',
  TAURI_UPDATE = 'tauri_update',
  TAURI_RELAUNCH = 'tauri_relaunch',
  TAURI_SESSION_MISMATCH = 'tauri_session_mismatch',
  TAURI_STOP_RECORDING_TO_PREVENT_MEMORY_LEAK_ERROR = 'tauri_stop_recording_to_prevent_memory_leak_error',
  TAURI_TRACK_FAILED_TO_RELAUNCH = 'TAURI_TRACK_FAILED_TO_RELAUNCH',
  TAURI_STOP_PREVENTED_AS_RECORDING_STILL_INITIALIZING = 'tauri_stop_recording_prevented_as_recording_still_initializing',

  // Sign In
  SignInClicked = 'sign_in_clicked',
  // Sign Up
  SignUpStarted = 'sign_up_started',
  SignUpClicked = 'sign_up_clicked',
  SignUpViaTeamInvite = 'sign_up_via_join_team_invite',
  SignUpSuccessful = 'sign_up_successful',
  SignUpPhoneNumberChanged = 'sign_up_phone_number_changed',
  //Session
  SessionCreate = 'session_create',
  SessionChangeLanguage = 'change_language',
  SessionUploadRecording = 'upload_recording',
  SessionUploadRecordingSuccess = 'upload_recording_success',
  SessionUploadRecordingFailed = 'upload_recording_failed',
  SessionUploadRecordingCancelled = 'upload_recording_cancelled',
  SessionDictation = 'session_dictation',
  SessionTranscribe = 'session_transcribe',
  SessionChooseMicInput = 'session_choose_mic_input',
  SessionStartRecording = 'session_start_recording',
  SessionStopRecording = 'session_stop_recording',
  SessionResumeRecording = 'session_resume_recording',
  SessionPauseRecording = 'session_pause_recording',
  SessionPatientDetailsUpdated = 'patient_details_updated',
  SessionDeleted = 'deleted',
  SessionCopyClicked = 'copy_clicked',
  SessionBatchDeleted = 'session_batch_deleted',
  SessionAllDeleted = 'session_all_deleted',
  SessionTemplateUpdated = 'template_updated',
  SessionVoiceUpdated = 'voice_updated',
  SessionCustomVoiceUpdated = 'custom_voice_updated',
  SessionDateTimeUpdated = 'session_date_time_updated',
  SessionEntered = 'session_entered',

  // context tab
  ContextAddContent = 'context_add_content',
  ContextUseDictation = 'context_use_dictation',

  // streaming
  NoteGenerationStart = 'note_generation_start',
  NoteGenerationEnd = 'note_generation_end',
  NoteGenerationError = 'note_generation_error',

  // new event to mark completed - can be error or good
  NoteGenerationCompleted = 'note_generation_completed',

  //Document
  DocumentCreateClick = 'document_create_click',
  DocumentReDo = 'document_redo',
  DocumentUndo = 'document_undo',
  /** @deprecated Use TrackingEventName.DocumentExported via trackEventV2 */
  DocumentExportPdf = 'document_export_pdf',
  /** @deprecated Use TrackingEventName.DocumentExported via trackEventV2 */
  DocumentExportDoc = 'document_export_doc',
  /** @deprecated Use TrackingEventName.DocumentExported via trackEventV2 */
  DocumentExportPrint = 'document_export_print',
  DocumentSelectionCopyAll = 'document_selection_copy_all',
  DocumentSelectionSendEmail = 'document_selection_send_email',
  DocumentSelectSetAsDefaultTemplate = 'document_selection_set_as_default_template',
  DocumentSelectionSaveAsNew = 'document_selection_save_as_new_template',
  DocumentSelectionViewStructure = 'document_selection_view_structure',
  DocumentSelectionRegenerateOutput = 'document_selection_regenerate_output',
  DocumentDeleted = 'document_deleted',
  DocumentRegeneratedByTemplate = 'document_regenerated_by_template',
  DocumentRegeneratedByAskAI = 'document_regenerated_by_ask_ai',
  DocumentCreatedByTemplate = 'created_by_template',
  DocumentCreatedByAskAI = 'created_by_ask_ai',
  DocumentCreatedByAICommand = 'created_by_ai_command',
  DocumentCreatedByFormFilling = 'created_by_form_filling',
  DocumentSyncChangesClicked = 'sync_changes_clicked',

  // Note
  NoteSelectionRegenerateOutput = 'selection_regenerate_output',
  NoteGeneratedByTemplate = 'generated_by_template',
  NoteGeneratedByPressAICommand = 'generated_by_press_ai_command',
  NoteGeneratedByAskAI = 'generated_by_ask_ai',

  // Feedback
  FeedbackModalSelectedRate = 'feedback_modal_selected_rate',
  FeedbackModalReasonSelected = 'feedback_modal_reason_selected',
  FeedbackSubmitted = 'feedback_submitted',

  //Template
  TemplateAskAi = 'template_ask_ai',
  templateAskAiByPreset = 'template_ask_ai_by_preset',
  TemplateUseExisting = 'template_use_existing',
  TemplateCreateNewClicked = 'template_create_new_clicked',
  TemplateCreatedUseAskAi = 'created_use_ask_ai',
  TemplateCreatedUseExistingClicked = 'created_use_existing_clicked',
  TemplateCreatedUseScratchClicked = 'created_use_scratch_clicked',
  TemplateDeleted = 'template_deleted',
  TemplateEdited = 'template_edited',
  // template manager
  TemplateLibrarySearched = 'template_searched',
  TemplateLibraryFilteredApplied = 'template_filtered_applied',
  TemplateLibraryViewed = 'template_library_viewed',
  TemplateLibrarySortApplied = 'template_sort_applied',
  TemplateFavourited = 'template_favourited',
  TemplatePinned = 'template_pinned',

  // Template Chat Builder
  OldTemplateTurnIntoNew = 'old_template_turn_into_new',
  CreateNewTemplate = 'create_new_template',
  ClickToTranscript = 'click_to_transcript',
  ClickToExample = 'click_to_example',
  NewTemplateEdit = 'new_template_edit',
  AddSection = 'add_section',
  DeleteSection = 'delete_section',
  UpdateTemplateInstructions = 'update_template_instructions',
  ClickDiscardTemplate = 'click_discard_template',
  SaveTemplate = 'save_template',
  ChangeVisibility = 'change_visibility',
  ChangeType = 'change_type',

  //AskAI
  AskAi = 'ask_ai',
  AskAiCopy = 'ask_ai_copy',
  AskAiAddToNote = 'ask_ai_add_to_note',
  AskAiReplaceNote = 'ask_ai_replace_note',
  AskAiAddAsNewOutput = 'ask_ai_add_as_new_output',
  AskAiRegenerate = 'ask_ai_regenerate',
  AskHeidiOpened = 'ask_heidi_opened',
  AskHeidiClosed = 'ask_heidi_closed',
  AskHeidiCitationClicked = 'citation_clicked',
  AskHeidiCsatModalOpened = 'ask_heidi_csat_modal_opened',

  // Settings
  SaveSettings = 'save_settings',
  SettingsManageBillingClicked = 'manage_billing_clicked',
  SettingsContactSalesClicked = 'contact_sales_clicked',
  EvidenceNhsContactSalesSubmitted = 'evidence_nhs_contact_sales_submitted',

  // Export Header/Footer Settings (User-level) — DEPRECATED: use trackEventV2 with ExportSettingToggled / ExportContentSaved
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  ExportHeaderEnabled = 'export_header_enabled',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  ExportHeaderDisabled = 'export_header_disabled',
  /** @deprecated Use TrackingEventName.ExportContentSaved via trackEventV2 */
  ExportHeaderContentSaved = 'export_header_content_saved',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  ExportFooterEnabled = 'export_footer_enabled',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  ExportFooterDisabled = 'export_footer_disabled',
  /** @deprecated Use TrackingEventName.ExportContentSaved via trackEventV2 */
  ExportFooterContentSaved = 'export_footer_content_saved',
  // Export Header/Footer Settings (Team-level) — DEPRECATED: use trackEventV2 with ExportSettingToggled / ExportContentSaved
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  TeamExportHeaderEnabled = 'team_export_header_enabled',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  TeamExportHeaderDisabled = 'team_export_header_disabled',
  /** @deprecated Use TrackingEventName.ExportContentSaved via trackEventV2 */
  TeamExportHeaderContentSaved = 'team_export_header_content_saved',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  TeamExportFooterEnabled = 'team_export_footer_enabled',
  /** @deprecated Use TrackingEventName.ExportSettingToggled via trackEventV2 */
  TeamExportFooterDisabled = 'team_export_footer_disabled',
  /** @deprecated Use TrackingEventName.ExportContentSaved via trackEventV2 */
  TeamExportFooterContentSaved = 'team_export_footer_content_saved',
  SettingsRevertToV1 = 'revert_to_v1',

  //integration
  IntegrationSubmitRequest = 'submit_integration_request',

  // Navigation
  NavigationRoadmap = 'roadmap',
  NavigationHelp = 'help',
  NavigationLogout = 'logout',
  NavigationGermanCall = 'german_call',
  NavigationFranceCall = 'france_call',
  NavigationSpanishCall = 'spanish_call',
  NavigationDiscoveryCall = 'discovery_call',
  NavigationGetStartedFAQ = 'get_started_faq',

  // Paywall
  PaywallCheckout = 'checkout',
  PaywallAddOnCheckout = 'checkout_add_on',
  PaywallCheckoutClickedFromUpgrade = 'paywall_checkout_from_upgrade',
  PaywallCheckoutClickedFromFreeToPaid = 'paywall_checkout_from_free_to_paid',

  // global configuration settings
  FindReplaceDocumentEdited = 'find_replace_document_edited',
  FindReplaceScopeJustNoteSelected = 'find_replace_scope_just_note_selected',
  FindReplaceScopeAllTabsSelected = 'find_replace_scope_all_tabs_selected',
  FindReplaceScopeEverywhereSelected = 'find_replace_scope_everywhere_selected',
  FindReplaceModalOpened = 'find_replace_modal_opened',
  FindReplaceModalCancelled = 'find_replace_modal_cancelled',
  FindReplaceModalSubmitted = 'find_replace_modal_submitted',
  UserSettingsDateFormatChanged = 'user_settings_date_format_changed',
  UserSettingsSpellingPreferenceChanged = 'user_settings_spelling_preference_changed',
  UserSettingsWordLibraryAdded = 'user_settings_word_library_added',

  // demo
  DemoAllowMic = 'demo_allow_microphone',
  DemoDisallowMic = 'demo_disallow_microphone',
  DemoSignUpFieldsLocation = 'demo_sign_up_fields_location',
  DemoSignUpFieldClinicianType = 'demo_sign_up_fields_clinician_type',
  DemoSignUpFieldMicrophoneType = 'demo_sign_up_fields_microphone_type',
  DemoStart = 'demo_start',
  DemoBeginTranscribing = 'demo_begin_transcribing',
  DemoStartRecording = 'demo_start_recording',
  DemoSeeTheTemplate = 'demo_see_the_template',
  DemoContinueClicked = 'demo_continue_clicked',
  DemoTryItNow = 'demo_try_it_now',
  DemoTryItOut = 'demo_try_it_out',
  // create session has session id sent
  DemoCreateSession = 'demo_create_session',
  DemoErrorTranscribing = 'demo_error_transcribing',
  DemoStopTranscribing = 'demo_stop_transcribing',
  DemoTranscriptTooShort = 'demo_transcript_too_short',
  DemoTranscriptTryAgain = 'demo_transcript_try_again',
  DemoTranscriptSeeExample = 'demo_transcript_see_example',
  DemoUseAI = 'demo_use_ai',
  DemoTryTemplate = 'demo_try_template',
  DemoJoinFree = 'demo_join_free',
  DemoJoinPremium = 'demo_join_premium',
  DemoSkipTour = 'demo_skip_tour',
  DemoClickHumanSupport = 'demo_click_human_support',
  DemoClickStartOnOwn = 'demo_click_start_on_own',
  DemoClickEmail = 'demo_click_email',
  DemoClickGmail = 'demo_click_gmail',
  // has session id, user id, saves selection during sign up
  DemoBindUserToOnboardingInfo = 'demo_bind_user_to_onboarding_info',
  DemoJumpRightInOnboarding = 'demo_jump_right_in_register',

  // Integrations
  IntegrationsSiteId = 'site_id',
  IntegrationsUserSearched = 'integration_user_searched',
  IntegrationsConnectIntegration = 'connect_integration',
  IntegrationsUpdateIntegration = 'update_integration',
  IntegrationSelectPatient = 'select_patient',

  //team
  TeamCreate = 'team_create',
  TeamUpdated = 'team_updated',
  SendTeamInvitation = 'send_team_invitation',
  RemoveTeamMember = 'remove_team_member',
  DeleteTeam = 'delete_team',
  AcceptTeamInvitation = 'accept_team_invitation',
  ResendTeamInvitation = 'resend_team_invitation',
  TeamBillingClicked = 'team_billing_clicked',
  JoinTeamSuccess = 'join_team_success',
  TeamMemberGuideVideoButtonClicked = 'team_member_guide_video_button_clicked',
  UseHeidiClicked = 'use_heidi_clicked',
  JoinUpdateSpecialtyClicked = 'join_update_specialty_clicked',
  SelfOnboardingSubmitClicked = 'self_onboarding_submit_clicked',
  // referral
  ReferralLinkCreated = 'referral_link_created',
  ReferralLinkCopied = 'referral_link_copied',
  ReferralModalOpened = 'referral_modal_opened',
  ReferralCouponAppliedToCheckout = 'referral_coupon_applied_to_checkout',

  OrganisationSurveyDismissed = 'organisation_survey_dismissed',
  OrganisationSurveyShown = 'organisation_survey_shown',
  OrganisationSurveyNameEntered = 'organisation_survey_name_entered',
  OrganisationSurveySizeSelected = 'organisation_survey_size_selected',
  OrganisationSurveyRoleSelected = 'organisation_survey_role_selected',
  OrganisationSurveyEhrProviderSelected = 'organisation_survey_ehr_provider_selected',
  OrganisationSurveySubmitted = 'organisation_survey_submitted',

  DeleteAccountClicked = 'delete_account_clicked',
  DeleteAccountSuccess = 'delete_account_success',

  IntegrationReadyModalClosedAndBack = 'integration_ready_modal_closed_and_back_to_ehr',
  IntegrationReadyModalClosedAndStay = 'integration_ready_modal_closed_and_stay_heidi',

  UserMfaToggle = 'user_mfa_toggle',
  TeamMfaToggle = 'team_mfa_toggle',
  TeamConsentToggle = 'team_consent_toggle',
  TeamCustomConsentTextUpdated = 'team_custom_consent_text_updated',

  // template community
  CommunityTemplateViewed = 'community_template_viewed',
  CommunityTemplateImported = 'community_template_imported',
  CommunityTemplatePublished = 'community_template_published',
  CommunityTemplateSearched = 'community_template_searched',
  CommunityTemplateLiked = 'community_template_liked',
  CommunityTemplateUnliked = 'community_template_unliked',

  // snippets
  CreateSnippet = 'create_snippet',
  EditSnippet = 'edit_snippet',
  DeleteSnippet = 'delete_snippet',
  UseSnippet = 'use_snippet',
  OpenSnippetPrompt = 'open_snippet_prompt',
  CreateSnippetsBatch = 'create_snippets_batch',
  CreateSnippetsBatchFailed = 'create_snippets_batch_failed',

  // replacements
  CreateReplacement = 'create_replacement',
  EditReplacement = 'edit_replacement',
  DeleteReplacement = 'delete_replacement',
  UseReplacement = 'use_replacement',
  BulkUploadReplacements = 'bulk_upload_replacements',

  // shortcuts
  ShortcutUsed = 'shortcut_used',

  // link sessions to patient profile
  LinkSessionsToPatientProfile = 'link_session_to_patient',
  UnlinkSessionsFromPatientProfile = 'unlink_session_from_patient',
  DeletePatientProfile = 'delete_patient_profile',
  CreatePatientProfile = 'create_patient_profile',

  // coding sidebar
  CodingSidebarOpened = 'coding_sidebar_opened',

  // notification preferences
  NotificationPreferencesSavedFromSettingsPage = 'notification_preferences_saved_from_settings_page',
  NotificationPreferencesSavedFromModal = 'notification_preferences_saved_from_modal',

  // getting started panel
  GettingStartedPanelOpened = 'getting_started_panel_opened',
  GettingStartedPanelClosed = 'getting_started_panel_closed',
  GettingStartedPanelNextStep = 'getting_started_panel_next_step',
  GettingStartedPanelOpenReferralModal = 'getting_started_panel_open_referral_modal',
  GettingStartedCompleted = 'getting_started_completed',

  // single file api call
  ContextFileUploaded = 'context_file_uploaded',
  ContextFileUploadFailed = 'context_file_upload_failed',
  ContextFileUploadSuccess = 'context_file_upload_success',

  // all files
  ContextFilesUploaded = 'context_files_uploaded',
  ContextFilesUpdatesSaved = 'context_files_updates_saved',
  // popup to attach files
  ContextFilesAttachmentPopupOpened = 'context_files_attachment_popup_opened',
  // delete file
  ContextFilesDeleted = 'context_files_deleted',
  // mark session as context only
  SessionMarkedAsContextOnly = 'session_marked_as_context_only',

  // chunk errors
  SessionChunkMismatchError = 'session_chunk_mismatch_error',
  BackupSizeZeroError = 'backup_size_zero_error',
  MediaRecorderUnableToStop = 'media_recorder_unable_to_stop',
  FailedToSendChunk = 'failed_to_send_chunk',

  // widget link
  WidgetLinkAccountUnlinkSwitch = 'widget_link_account_unlink',
  WidgetLinkAccountStartTrialClicked = 'widget_link_account_start_trial_clicked',
  WidgetLinkUpgradeComplete = 'widget_link_upgrade_complete',
  WidgetLinkHeidiAccountAlreadyLinkedToEhr = 'widget_link_heidi_account_already_linked_to_ehr',
  WidgetLinkEhrAccountAlreadyLinkedToHeidi = 'widget_link_ehr_account_already_linked_to_heidi',

  // Reverse Trial Modals
  // Trial to Paid Modal (Pro)
  TrialToPaidModalOpened = 'trial_to_paid_modal_opened',
  TrialToPaidModalClosed = 'trial_to_paid_modal_closed',
  TrialToPaidModalPeriodChanged = 'trial_to_paid_modal_period_changed',
  TrialToPaidModalUpgradeClicked = 'trial_to_paid_modal_upgrade_clicked',

  // End Trial Modal
  EndTrialModalOpened = 'end_trial_modal_opened',
  EndTrialModalClosed = 'end_trial_modal_closed',
  EndTrialModalPeriodChanged = 'end_trial_modal_period_changed',
  EndTrialModalUpgradeClicked = 'end_trial_modal_upgrade_clicked',
  EndTrialModalDowngradeClicked = 'end_trial_modal_downgrade_clicked',
  EndTrialModalExtendClicked = 'end_trial_modal_extend_clicked',

  // Extend Free Trial Modal
  ExtendTrialModalOpened = 'extend_trial_modal_opened',
  ExtendTrialModalClosed = 'extend_trial_modal_closed',
  ExtendTrialModalDurationChanged = 'extend_trial_modal_duration_changed',
  ExtendTrialModalExtendClicked = 'extend_trial_modal_extend_clicked',
  ExtendTrialModalBackClicked = 'extend_trial_modal_back_clicked',

  FailedToStartChunkRecording = 'failed_to_start_chunk_recording',
  FailedToStartMediaRecorderChunk = 'failed_to_start_chunk_media_recorder',
  FailedTrackStatusInChunk = 'failed_track_status_in_chunk',
  FailedTrackStatusInBackup = 'failed_track_status_in_backup',
  RecordingTriesToStartWhenStatusIsRecording = 'recording_tries_to_start_when_status_is_recording',

  // Chrome Extension
  ExtensionSetToken = 'chrome_extension_set_token',

  // Customise Template Menu
  ProScribeTemplateQuotaExceeded = 'pro_template_quota_exceeded',

  GotFirstNonEmptyTranscriptChunk = 'got_first_non_empty_transcript_chunk',
  ReachedAudioLevelDuringRecording = 'reached_audio_level_during_recording',

  // Form Filling
  FormFillCreateTemplateClicked = 'create_template_clicked',
  FormFillHelpArticleClicked = 'help_article_clicked',
  FormFillUploadClicked = 'upload_clicked',
  FormLibraryViewed = 'form_library_viewed',

  // Pyne
  OnboardingFlowRendered = 'onboarding_flow_rendered',

  // Personalization
  PersonalizationTurnedOff = 'personalization_turned_off',
  PersonalizationTurnedOn = 'personalization_turned_on',

  // Chat
  ChatMessageSubmitted = 'chat_message_submitted',
  ChatStreamInterrupted = 'chat_stream_interrupted',
  ChatActivityTimelineOpened = 'chat_activity_timeline_opened',
  ChatActivityTimelineClosed = 'chat_activity_timeline_closed',
  ChatModeToggled = 'chat_mode_toggled',

  // Global Template
  GlobalTemplateVisibilityChanged = 'global_template_visibility_changed',
  GlobalTemplateDeleted = 'global_template_deleted',
  GlobalTemplateCreated = 'global_template_created',
  GlobalTemplateUsed = 'global_template_used',
}

/**
 * The keyboard-shortcut vocabulary of the v1 `shortcut_used` event.
 *
 * Lives here rather than in the model because it is a VALUE: a type import is
 * erased, an enum import is not, so naming a shortcut from
 * `src/lib/analytics/**` through the model would pull the whole model in.
 */
export enum ShortcutContext {
  // shortcuts context
  NEW_SESSION = 'new_session',
  START_STOP_RECORDING = 'start_stop_recording',
  PAUSE_RESUME_RECORDING = 'pause_resume_recording',
  CHANGE_TAB = 'change_tab',
  CHANGE_SESSION = 'change_session',
  DELETE_SELECTED_SESSIONS = 'delete_selected_sessions',
  GO_TO_SESSIONS = 'go_to_sessions',
  GO_TO_TEMPLATE_LIBRARY = 'go_to_template_library',
  GO_TO_ACCOUNT = 'go_to_account',
  GO_TO_PREFERENCE = 'go_to_preference',
  GO_TO_TEAM = 'go_to_team',
  GO_TO_COMMUNITY = 'go_to_community',
  GO_TO_MEMORY = 'go_to_memory',
  OPEN_SHORTCUTS_HELPER = 'open_shortcuts_helper',
  OPEN_SIDE_NAV = 'open_side_nav',
  PIN_UNPIN_SIDEBAR_PANEL = 'pin_unpin_sidebar_panel',
  DOCUMENT_PRINT = 'document_print',
  CREATE_DOCUMENT = 'create_document',
  CREATE_TEMPLATE = 'create_template',
  CHANGE_TEMPLATE = 'change_template',
  CHANGE_BRAIN = 'change_brain',
  CHANGE_VOICE = 'change_voice',
  PT_CONSENT = 'pt_consent',
}

/**
 * The action half of every v1 `evidence_*` event.
 *
 * Promoted out of the model with `ShortcutContext` and `TaskEventType` and
 * exported here for the same reason: the emit hosts under
 * `src/lib/analytics/scribe/` name these members.
 */
export enum SegmentEvidenceActionName {
  EvidenceSidebarClicked = 'sidebar_clicked',
  EvidenceSourcesListClicked = 'sources_list_clicked',
  EvidenceSourcesListPresetClicked = 'sources_list_preset_clicked',
  EvidenceSourcesListCustomClicked = 'sources_list_custom_clicked',
  EvidenceSourcesListLocalGuidelinesClicked = 'sources_list_local_guidelines_clicked',
  EvidenceSourcesListWebSearchClicked = 'sources_list_web_search_clicked',
  EvidenceChatSubmitted = 'chat_submitted',
  EvidenceChatCompleted = 'chat_completed',
  EvidenceChatError = 'chat_error',
  EvidenceChatAborted = 'chat_aborted',
  EvidenceProTrialButtonStarted = 'pro_trial_button_started',
  EvidenceTrialStarted = 'trial_started',
  EvidenceDeepLinkSignInClicked = 'deep_link_sign_in_clicked',
  EvidenceDeepLinkSignUpClicked = 'deep_link_sign_up_clicked',
  EvidenceNewChatClicked = 'new_chat_clicked',
  EvidenceDeleteChatClicked = 'delete_chat_clicked',
  EvidenceClickPastSessionClicked = 'click_past_session_clicked',
  EvidenceChatSidebarOpen = 'chat_sidebar_open',
  EvidenceChatSidebarClose = 'chat_sidebar_close',
  EvidenceToastShown = 'toast_shown',
  EvidenceToastViewClicked = 'toast_view_clicked',
  EvidenceLanguageChanged = 'language_changed',
  EvidenceSpecialtyChanged = 'specialty_changed',
  EvidenceJurisdictionChanged = 'jurisdiction_changed',
  EvidenceOnboardingViewed = 'onboarding_viewed',
  EvidenceOnboardingSaved = 'onboarding_saved',
  EvidenceOnboardingSkipped = 'onboarding_skipped',
  EvidenceOnboardingDismissed = 'onboarding_dismissed',
  EvidenceShareModalOpened = 'share_modal_opened',
  EvidenceShareLinkCopied = 'share_link_copied',
  EvidenceShareSocialClicked = 'share_social_clicked',
  EvidenceSharedChatOpened = 'shared_chat_opened',
  EvidenceNhsContactSalesSubmitted = 'nhs_contact_sales_submitted',
}

/**
 * The action half of every v1 `task_*` event.
 */
export enum TaskEventType {
  TaskSuggested = 'suggested',
  TaskSuggestedCompleted = 'suggested_completed',
  TaskDeleted = 'deleted',
  TaskCompleted = 'completed',
  // TaskAutoArchived = 'task_auto_archived',
  TaskCreatedManual = 'created_manual',
  TaskCreatedInSession = 'created_in_session',
  TaskEdited = 'edited',
  TaskRestored = 'restored',
  TaskListViewInteraction = 'list_interaction',
  MasterListViewed = 'master_list_viewed',
  ScribeSessionStarted = 'scribe_session_started',

  TaskPanelToggleOpenedFromSession = 'panel_opened_from_session',
  TaskPanelToggleClosedFromSession = 'panel_closed_from_session',
  TaskPanelToggleClosedViaX = 'panel_closed_via_x',

  TaskEvidenceClicked = 'evidence_clicked',

  TaskViewed = 'task_viewed',
  TaskListViewed = 'task_list_viewed',
  TaskNotificationClicked = 'task_notification_clicked',
}
